/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useState } from 'react';
import { Select, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { DROPDOWN_EVENT_MAP, DropdownOptions, IDropdownWithButtonProps } from './AllSchedules.Model';
import { JobManagementType } from '../AllJobs/AllJobs.Model';
import { Labels } from '../../../../Shared/Components/Common/GraphContainer/GraphContainer';
import { setFilterState } from '../../../../Shared/Store/features/filtersSlice';

import { JobType, Paths } from '../../../../Shared/Constants/App';
import { hasEventId } from '../../../../Shared/Utilities/utils';

import { Icons } from '../../../../Shared/Constants/Icons';

import { Options } from '../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import { SelectActionInput } from './AllJobs.Style';

const DropdownWithButton = ({
  selectedRows,
  type,
  setSelectedDropDownValue,
  selectedDropDownValue,
  filters,
  processEvents,
}: IDropdownWithButtonProps): JSX.Element => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { t } = useTranslation(['jobManagement']);

  const allSchedulesOptions = [
    { value: DropdownOptions.RescheduleShift, label: t('jobManagement:rescheduleShift') },
    { value: DropdownOptions.CancelShift, label: t('jobManagement:cancelShift') },
    ...(selectedRows?.length === 1
      ? [{ value: DropdownOptions.ModifySchedule, label: t('jobManagement:modifyJobSchedule') }]
      : []),
    { value: DropdownOptions.ManageAssociatesAndSupervisors, label: 'Manage Associates And Supervisors' },
    { value: DropdownOptions.AdjustProof, label: t('jobManagement:modifyProofSettings') },
    ...(selectedRows?.length === 1 && [JobType.Recurring, JobType.Periodic].includes(selectedRows?.[0]?.jobType)
      ? [{ value: DropdownOptions.CreateAdhocShifts, label: t('jobManagement:createUnScheduleWork') }]
      : []),
    ...(selectedRows?.length === 1 && [JobType.Recurring, JobType.Periodic].includes(selectedRows?.[0]?.jobType)
      ? [{ value: DropdownOptions.ModifyServices, label: t('jobManagement:modifyServices') }]
      : []),
  ]?.filter((option) => {
    const eventId = DROPDOWN_EVENT_MAP[option.value];
    if (!eventId) return false;
    return processEvents && hasEventId(processEvents, eventId);
  });

  const allJobsOptions = [
    ...(selectedRows?.length === 1
      ? [{ value: DropdownOptions.RemoveVendor, label: t('jobManagement:removeVendor') }]
      : []),
    ...(selectedRows?.length === 1
      ? [{ value: DropdownOptions.ReassignVendor, label: t('jobManagement:reassignVendor') }]
      : []),
    { value: DropdownOptions.ModifyGeoFence, label: t('jobManagement:modifyGeoFence') },
    ...(selectedRows?.length === 1 && [JobType.Recurring, JobType.Periodic].includes(selectedRows?.[0]?.jobType)
      ? [{ value: DropdownOptions.CreateJobSchedule, label: t('jobManagement:createJobSchedule') }]
      : []),
    ...(selectedRows?.length === 1 && [JobType.Recurring, JobType.Periodic].includes(selectedRows?.[0]?.jobType)
      ? [{ value: DropdownOptions.CreateAdhocShifts, label: t('jobManagement:createUnScheduleWork') }]
      : []),
    ...(selectedRows?.length === 1 && [JobType.Recurring, JobType.Periodic].includes(selectedRows?.[0]?.jobType)
      ? [{ value: DropdownOptions.ModifyServices, label: t('jobManagement:modifyServices') }]
      : []),
    ...(selectedRows?.length === 1 && [JobType.Recurring].includes(selectedRows?.[0]?.jobType)
      ? [{ value: DropdownOptions.CreatePeriodicJob, label: t('jobManagement:createPeriodicJob') }]
      : []),
    ...(selectedRows?.length === 1 &&
    [JobType.Recurring, JobType.Periodic].includes(selectedRows?.[0]?.jobType) &&
    selectedRows?.[0]?.parentWorkOrderId
      ? [{ value: DropdownOptions.CreateRetrospectiveShift, label: t('jobManagement:createRetrospectiveShift') }]
      : []),
  ]?.filter((option) => {
    const eventId = DROPDOWN_EVENT_MAP[option.value];
    if (!eventId) return false;
    return processEvents && hasEventId(processEvents, eventId);
  });

  const options =
    type === JobManagementType.AllSchedules
      ? allSchedulesOptions
      : type === JobManagementType.AllJobs
        ? allJobsOptions
        : [];

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  const handleValueChange = (event: any) => {
    const value = event.target.value;
    setSelectedDropDownValue?.(value);
    if (filters) {
      dispatch(
        setFilterState({
          jobName: filters?.jobName,
          jobNumber: filters?.jobNumber,
          jobAddress: filters?.jobAddress,
          customer: filters?.customer,
        })
      );
    }
    if (value === DropdownOptions.RescheduleShift) {
      navigate('/all-schedules/reschedule-shift', {
        state: {
          selectedRows: selectedRows,
        },
      });
    } else if (value === DropdownOptions.CancelShift) {
      navigate('/all-schedules/cancel-shift', {
        state: {
          selectedRows: selectedRows,
        },
      });
    } else if (value === DropdownOptions.ModifySchedule) {
      navigate('/all-schedules/modify-job-schedule', {
        state: {
          selectedRows: selectedRows,
        },
      });
    } else if (value === DropdownOptions.ManageAssociatesAndSupervisors) {
      navigate('/all-jobs/manage-associates-and-supervisors', {
        state: {
          selectedRows: selectedRows,
        },
      });
    } else if (value === DropdownOptions.AdjustProof) {
      navigate('/all-schedules/modify-proof-settings', {
        state: {
          selectedRows: selectedRows,
        },
      });
    } else if (value === DropdownOptions.CreateJobSchedule) {
      navigate(Paths.CreateJobSchedule, {
        state: {
          selectedRows: selectedRows,
        },
      });
    } else if (value === DropdownOptions.ModifyServices) {
      navigate(Paths.ModifyServices, {
        state: {
          selectedRows: selectedRows,
        },
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Select
        value={selectedDropDownValue}
        onChange={handleValueChange}
        open={isDropdownOpen}
        onClose={toggleDropdown}
        onOpen={toggleDropdown}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return t('jobManagement:selectAction');
          }
          const selectedOption = options?.find((option) => option.value === selected);
          return selectedOption ? selectedOption.label : t('jobManagement:selectAction');
        }}
        input={<SelectActionInput />}
        IconComponent={() => (
          <img
            src={Icons.DropdownIcon}
            alt="dropdown-icon"
            style={{
              transform: isDropdownOpen ? Labels.Rotate : 'none',
              transition: 'transform 0.3s',
              position: 'absolute',
              right: '0.75rem',
              cursor: 'pointer',
            }}
            onClick={toggleDropdown}
          />
        )}>
        {options.map((option) => (
          <Options key={option.value} value={option.value}>
            {option.label}
          </Options>
        ))}
      </Select>
    </Box>
  );
};

export default DropdownWithButton;
